import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import {MergeError} from '../../helpers/merge-error'
import type {PullRequestMergeMethod} from '../../types'

/**
 * Enqueues stacked pull requests, starting from the given pull request.
 *
 * To run additional callbacks, you can pass any supported callback option to the `mutate` function when it's
 * called, including `onSuccess`, `onError`, and `onSettled`. These additional callbacks will run after the
 * callbacks defined in the hook below.
 *
 * For more details, see https://tanstack.com/query/latest/docs/framework/react/guides/mutations#mutation-side-effects
 *
 * @example
 * ```ts
 * const {mutate} = useEnqueueStackMutation()
 *
 * // Required arguments
 * const mutationArguments = {mergeMethod: 'SQUASH'}
 *
 * // Optional callbacks will run after callbacks defined in useEnqueueStackMutation hook
 * const mutationCallbacks = {
 *   onError: error => console.log(error.message),
 *   onSuccess: (data) => console.log(data),
 * }
 *
 * mutate(mutationArguments, mutationCallbacks)
 * ```
 */
type MutationData = {
  authorEmail?: string | null | undefined
  bypassBranchProtections?: boolean
  commitMessage?: string | null | undefined
  commitTitle?: string | null | undefined
  mergeMethod: PullRequestMergeMethod
}

export function useEnqueueStackMutation({
  onError,
  onSuccess,
}: {
  onError: (error: Error) => void
  onSuccess?: () => void
}) {
  const apiURL = usePageDataUrl(PageData.enqueueStack)
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })
      const json = await parseJSONWithBetterErrors(response)
      const predefinedError = new MergeError(
        json?.error || 'Unknown error occurred',
        json?.metadata?.ruleErrors || [],
        response.status,
      )
      throwErrorsIfBadResponse(response, json, predefinedError)
      return json
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
      return onSuccess?.()
    },
    onError: (e: Error) => onError(e),
  })
}
